import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import type { RootState } from "../../../store/store";
import { motion, AnimatePresence } from "motion/react";

import DashboardCards from "./UserDashboardComponents/DashboardCards";
import DashboardGraphs from "./UserDashboardComponents/DashboardGraphs";
import DashboardPOs from "./UserDashboardComponents/DashboardPOs";
import UserCreatePOForm from "./UserDashboardComponents/UserCreatePOForm";
import RepairReplacementForm from "./UserDashboardComponents/Repair/RepairReplacementForm";
import StockForm from "./UserDashboardComponents/Stock/StockForm";

import RepairDashboardCards from "./UserDashboardComponents/Repair/RepairDashboardCards";
import StockDashboardCards from "./UserDashboardComponents/Stock/StockDashboardCards";
import RepairDashboardGraphs from "./UserDashboardComponents/Repair/RepairDashboardGraphs";
import StockDashboardGraphs from "./UserDashboardComponents/Stock/StockDashboardGraphs";
import DashboardRepairList from "./UserDashboardComponents/Repair/DashboardRepairList";
import DashboardStockList from "./UserDashboardComponents/Stock/DashboardStockList";

type SelectedForm = "select" | "po" | "repair" | "stock" | null;
type SelectedData = "po" | "repair" | "stock";

const UserDashboard: React.FC = () => {
  const [selectedForm, setSelectedForm] = useState<SelectedForm>(null);
  const [selectedData, setSelectedData] = useState<SelectedData>("po");
  const [ordersUpdated, setOrdersUpdated] = useState(false);

  const triggerOrderRefresh = () => setOrdersUpdated((v) => !v);

  const location = useLocation();
  const { user } = useSelector((s: RootState) => s.auth);
  const [fetchedDashboardPermissions, setFetchedDashboardPermissions] = useState<any | null>(null);
  const [fetchedDashboardSections, setFetchedDashboardSections] = useState<any | null>(null);
  const [repairCardFilter, setRepairCardFilter] = useState<
    | "all"
    | "repair"
    | "replacement"
    | "pending"
    | "delayed"
    | "approved"
  >("all");

  useEffect(() => {
    // If the auth user object doesn't include dashboard permissions, try fetching them
    const fetchIfMissing = async () => {
      try {
        if (user && !(user.dashboardPermissions || user.dashboardSections)) {
          const { getDashboardPermissionsAPI } = await import("../../../utils/api");
          const id = user.id || user._id;
          if (!id) return;
          const resp = await getDashboardPermissionsAPI(id);
          if (resp) {
            setFetchedDashboardPermissions(resp.dashboardPermissions || null);
            setFetchedDashboardSections(resp.dashboardSections || null);
          }
        }
      } catch (err) {
        // silent fail — UI will fall back to defaults
        console.error("Failed to fetch dashboard permissions in UserDashboard:", err);
      }
    };
    fetchIfMissing();
  }, [user]);

  // Dashboard permissions from user object, fallback to fetched values, then defaults
  // Default to NO access for users with no explicit permissions
  // Default to NO access for users with no explicit permissions
  let userDashboardPermissions =
    user?.dashboardPermissions || fetchedDashboardPermissions || {
      po: { cards: false, graphs: false, table: false },
      repair: { cards: false, graphs: false, table: false },
      stock: { cards: false, graphs: false, table: false },
    };

  let userSectionsEnable =
    user?.dashboardSections || fetchedDashboardSections || {
      po: false,
      repair: false,
      stock: false,
    };

  // Admins and subadmins should see all dashboard components by default
  if (user && (user.userType === "admin" || user.userType === "subadmin")) {
    userDashboardPermissions = {
      po: { cards: true, graphs: true, table: true },
      repair: { cards: true, graphs: true, table: true },
      stock: { cards: true, graphs: true, table: true },
    };
    userSectionsEnable = { po: true, repair: true, stock: true };
  }

  // If a normal 'user' account doesn't have explicit permissions configured,
  // enable the basic dashboard sections by default so users can see PO/Repair/Stock.
  if (user && user.userType === "user" && !(user.dashboardPermissions || fetchedDashboardPermissions)) {
    userDashboardPermissions = {
      po: { cards: true, graphs: true, table: true },
      repair: { cards: true, graphs: true, table: true },
      stock: { cards: true, graphs: true, table: true },
    };
    userSectionsEnable = { po: true, repair: true, stock: true };
  }

  const formOptions = [
    { id: "po" as const, title: "Purchase Order", description: "Create a new purchase order", icon: "📋" },
    { id: "repair" as const, title: "Repair / Replacement", description: "Request repair or replacement", icon: "🔧" },
    { id: "stock" as const, title: "Stock Request", description: "Request new stock items", icon: "📦" },
  ];

  return (
    <div>
      {/* Create / Request Button */}
      <div className="flex justify-end w-full mb-4">
        {location.pathname === "/user/dashboard" && (
          <button
            className="bg-white text-[var(--theme-color)] px-3 py-3 lg:p-5 xl:p-5 rounded-lg font-semibold cursor-pointer inline-flex items-center justify-between min-w-full text-xl border shadow-md hover:bg-blue-50 duration-200 dark:bg-zinc-900 dark:text-white"
            onClick={() => setSelectedForm("select")}
          >
            <span>Create / Request</span>
            <span className="bg-[var(--theme-color)] dark:bg-zinc-200 text-white dark:text-black px-3 text-2xl xl:text-4xl lg:text-4xl rounded ml-2 flex items-center pb-2">+</span>
          </button>
        )}
      </div>

      {/* Data Selector Tabs */}
      <div className="flex gap-1 mb-6">
        {userSectionsEnable.po && (
          <button
            onClick={() => setSelectedData("po")}
            className={`px-5 py-2 rounded-lg font-semibold border shadow ${selectedData === "po" ? "bg-[var(--theme-color)] text-white" : "bg-white"}`}
          >
            Purchase Orders
          </button>
        )}
        {userSectionsEnable.repair && (
          <button
            onClick={() => setSelectedData("repair")}
            className={`px-5 py-2 rounded-lg font-semibold border shadow ${selectedData === "repair" ? "bg-[var(--theme-color)] text-white" : "bg-white"}`}
          >
            Repair / Replacement
          </button>
        )}
        {userSectionsEnable.stock && (
          <button
            onClick={() => setSelectedData("stock")}
            className={`px-5 py-2 rounded-lg font-semibold border shadow ${selectedData === "stock" ? "bg-[var(--theme-color)] text-white" : "bg-white"}`}
          >
            Stock Requests
          </button>
        )}
      </div>

      {/* Dashboard Content */}
      {selectedData === "po" && userSectionsEnable.po && (
        <>
          {userDashboardPermissions.po.cards && <DashboardCards />}
          {userDashboardPermissions.po.graphs && <DashboardGraphs />}
          {userDashboardPermissions.po.table && <DashboardPOs refreshTrigger={ordersUpdated} />}
        </>
      )}

      {selectedData === "repair" && userSectionsEnable.repair && (
        <>
          {userDashboardPermissions.repair.cards && (
            <RepairDashboardCards setCardFilter={setRepairCardFilter} cardFilter={repairCardFilter} />
          )}
          {userDashboardPermissions.repair.graphs && <RepairDashboardGraphs />}
          {userDashboardPermissions.repair.table && <DashboardRepairList refreshTrigger={ordersUpdated} />}
        </>
      )}

      {selectedData === "stock" && userSectionsEnable.stock && (
        <>
          {userDashboardPermissions.stock.cards && <StockDashboardCards />}
          {userDashboardPermissions.stock.graphs && <StockDashboardGraphs />}
          {userDashboardPermissions.stock.table && <DashboardStockList refreshTrigger={ordersUpdated} />}
        </>
      )}

      {/* --- Select Form Modal --- */}
      <AnimatePresence>
        {selectedForm === "select" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md px-4"
            onClick={() => setSelectedForm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-2xl p-8 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Select Request Type</h2>
                <p className="text-gray-500 dark:text-gray-400">Choose what you'd like to create</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {formOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSelectedForm(option.id)}
                    className="group relative overflow-hidden rounded-xl p-6 bg-white dark:bg-zinc-800 border-2 border-gray-200 dark:border-zinc-700 hover:border-transparent shadow-md hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="relative z-10 flex flex-col items-center text-center">
                      <span className="text-5xl mb-3">{option.icon}</span>
                      <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">{option.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{option.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSelectedForm(null)}
                className="w-full py-3 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- PO Form Modal --- */}
      <AnimatePresence>
        {selectedForm === "po" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
            <UserCreatePOForm setShowForm={() => setSelectedForm(null)} onOrderCreated={() => { triggerOrderRefresh(); setSelectedForm(null); }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- Repair Form Modal --- */}
      <AnimatePresence>
        {selectedForm === "repair" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
            <RepairReplacementForm onCancel={() => setSelectedForm(null)} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- Stock Form Modal --- */}
      <AnimatePresence>
        {selectedForm === "stock" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
            <StockForm onClose={() => { triggerOrderRefresh(); setSelectedForm(null); }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDashboard;
